import { Courses } from './../courses';
import { Action, Module } from '@vuex/decorators';
import { TablePanel } from 'table-panel';

import { auth, RoleId } from '@auth';
import { modals } from '@modals';
import { Invite, Organization } from '@models';
import { alert } from '@services/alert';
import { clipboard } from '@services/clipboard';
import { store } from '@store';

import TableCellInviteStatus from '@components/TableCells/TableCellInviteStatus.vue';
import TableCellUserEmail from '@components/TableCells/TableCellUserEmail.vue';
import { faCopy } from '@icons/solid/faCopy';
import { faEnvelope } from '@icons/solid/faEnvelope';
import { faAddressCard } from '@icons/solid/faAddressCard';
import { faEye } from '@icons/solid/faEye';
const columns: TablePanel.Column<Invite>[] = [
    {
        key: 'createdAt',
        label: 'Created',
        type: 'date',
        value: 'createdAt',
    },
    {
        key: 'email',
        label: 'Email',
        value: 'email',
        component: TableCellUserEmail,
    },
    {
        key: 'organization',
        label: 'Organization',
        value: ({ organization }) => organization?.name,
        placeholder: 'N/A',
        filterByFormatted: true,
        sortByFormatted: true,
        hidden: ({ selectedRole }) => selectedRole?.roleId !== RoleId.LasAdmin,
    },
    {
        key: 'status',
        label: 'Status',
        component: TableCellInviteStatus,
        value: (item) => item,
    },
    {
        key: 'expirationDate',
        label: 'Invite Expires',
        type: 'date',
        value: 'expirationDate',
    },
];

const operations: TablePanel.Row.Operation<Invite>[] = [
    {
        label: 'Get Invite Link',
        icon: faCopy,
        fn: ({ id }) => {
            void clipboard.copy(`${location.origin}/register/${id}`);
        },
    },
    {
        label: 'Resend Invite',
        icon: faEnvelope,
        fn: ({ organization, id }) => {
            if (!organization?.id) return;
            void resendInvite(organization?.id, id);
        },
        hidden: ({ used, organization }) => {
            return used || !organization?.id;
        },
    },
    {
        label: 'View User',
        icon: faAddressCard,
        fn: ({ receiver }) => {
            if (!receiver) return;
            void modals.user.details({ user: receiver });
        },
        hidden() {
            return !auth.isActiveRole(RoleId.LasAdmin);
        },
    },
    {
        label: 'View',
        icon: faEye,
        fn: (invite) => {
            void modals.util.viewValue({ value: invite });
        },
        hidden() {
            return !auth.isActiveRole(RoleId.LasAdmin);
        },
    },
];

const filterFields = ['email', 'createdAt', 'institution'];

const specialFilters = [
    {
        type: 'dropdown',
        label: 'Accepted',
        options: [
            { value: '--', text: '--' },
            { value: true, text: 'Yes' },
            { value: false, text: 'No' },
        ],
        handler: (item: Invite, choice: unknown) => {
            return item.accepted === choice;
        },
    },
];

@Module({ namespaced: true })
export class CourseInvitesTablePanel
    extends TablePanel<Invite>
    implements TablePanel.Props<Invite> {
    onPageChanged?: TablePanel.OnPageChangedCallback<Invite>;
    readonly module = 'invites';
    readonly loadAction = 'searchByCourse';
    readonly pk = 'id';
    readonly label = 'COURSE INVITES';
    readonly columns = columns;
    readonly operations = operations;
    readonly filterFields = filterFields;
    readonly deleteAction = 'deleteInvite';
    readonly tableSortType = 'createdAt';
    readonly tableSortReverse = true;
    readonly specialFilters = specialFilters;

    get enabled() {
        return this.isActiveRole(4, 5, 7, 9);
    }

    get progressive() {
        return this.isActiveRole(RoleId.LasAdmin);
    }

    get courseId() {
        // get course id from url
        const url = new URL(window.location.href);
        let urlArray = url.pathname.split('/');
        // get the index of "course"
        const courseIndex = urlArray.indexOf('course');
        // get the course id
        const courseId = urlArray[courseIndex + 1];
        return courseId;
    }

    /** ... */
    get tableMenu() {
        const roleId = this.activeUser.selectedRole?.roleId;

        const items: TablePanel.MenuItem[] = [];

        if (
            roleId === RoleId.Instructor ||
            roleId === RoleId.InstitutionAdmin ||
            roleId === RoleId.LasAdmin
        ) {
            items.push({
                key: 'inviteStudent',
                label: 'Invite Student(s)',
                click: () => void modals.user.inviteStudent({ targetCourseId: this.courseId ?? '' }),
            });
        }

        return items;
    }

    @Action
    async deleteInvite({ items }: InvitesTablePanel.DeleteItemOptions) {
        await modals.confirm.deleteItems({ itemType: 'invites', items });

        void this.context.dispatch('load');
    }
}

async function resendInvite(
    organizationId: Organization['id'],
    inviteId: Invite['id'],
) {
    try {
        await store.dispatch('invites/resend', {
            organizationId,
            invites: [inviteId],
        });
    } catch (err) {
        return alert.error(err);
    }

    alert.success('Invite Resent');
}

export namespace InvitesTablePanel {
    /** ... */
    export interface DeleteItemOptions {
        items: Invite[];
    }

    /** ... */
    export interface ResendItemOptions {
        invite: Invite;
    }
}
